import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, Zap, ArrowRight } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { siteHeaderApi, assetUrl, contactApi } from '../services/api';

type SiteHeaderSettings = {
  header_style: 'style1' | 'style2' | 'style3';
  logo_url?: string;
  button_text?: string;
  button_link?: string;
  button_color?: string;
  button_hover_color?: string;
  text_color?: string;
  menu_position?: 'left' | 'center' | 'right';
  is_active?: boolean;
  animation_type?: string;
  custom_css?: string;
};

type HeaderMenuItem = {
  id: number;
  page_id: number;
  menu_text: string;
  custom_url?: string;
  menu_order: number;
  is_active: boolean;
  page_slug: string;
  page_title: string;
};

interface NavbarProps {
  onContactClick: () => void;
}

export const Navbar: React.FC<NavbarProps> = ({ onContactClick }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [hoveredLink, setHoveredLink] = useState<string | null>(null);
  const [settings, setSettings] = useState<SiteHeaderSettings | null>(null);
  const [menuItems, setMenuItems] = useState<HeaderMenuItem[]>([]);
  const [whatsappNumber, setWhatsappNumber] = useState<string>('');

  const fallbackMenuItems: HeaderMenuItem[] = [
    { id: -1, page_id: 0, menu_text: 'Home', custom_url: '/', menu_order: 1, is_active: true, page_slug: 'home', page_title: 'Home' },
    { id: -2, page_id: 0, menu_text: 'About', custom_url: 'about', menu_order: 2, is_active: true, page_slug: 'about', page_title: 'About' },
    { id: -3, page_id: 0, menu_text: 'Services', custom_url: 'services', menu_order: 3, is_active: true, page_slug: 'services', page_title: 'Services' },
    { id: -4, page_id: 0, menu_text: 'Portfolio', custom_url: '/portfolio', menu_order: 4, is_active: true, page_slug: 'portfolio', page_title: 'Portfolio' },
    { id: -5, page_id: 0, menu_text: 'Projects', custom_url: '/projects', menu_order: 5, is_active: true, page_slug: 'projects', page_title: 'Projects' },
  ];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const headerRes = await siteHeaderApi.get();
        if (headerRes?.success && headerRes.data) {
          setSettings({
            ...headerRes.data,
            header_style: 'style2',
            button_text: headerRes.data.button_text || 'Get in Touch',
            button_link: headerRes.data.button_link || '#',
            text_color: headerRes.data.text_color || '#ffffff',
            button_color: headerRes.data.button_color || '#ff6b35',
            button_hover_color: headerRes.data.button_hover_color || '#ff8a5c',
            is_active: headerRes.data.is_active ?? true,
          });
        }

        const menuRes = await siteHeaderApi.getMenuItems();
        if (menuRes?.success && Array.isArray(menuRes.data)) {
          const activeItems = menuRes.data.filter((item: HeaderMenuItem) => item.is_active);
          setMenuItems(activeItems.length ? activeItems : fallbackMenuItems);
        } else {
          setMenuItems(fallbackMenuItems);
        }

        // Fetch WhatsApp number from contact settings
        try {
          const contactRes = await contactApi.getPublicSettings();
          if (contactRes?.success && contactRes.data) {
            if (contactRes.data.whatsapp_number) {
              setWhatsappNumber(contactRes.data.whatsapp_number);
            } else if (contactRes.data.phone_number) {
              // Fallback to phone_number if whatsapp_number is not set
              setWhatsappNumber(contactRes.data.phone_number);
            }
          }
        } catch (err) {
          console.error('Error fetching WhatsApp number:', err);
        }
      } catch (error) {
        console.error('Error fetching header data:', error);
        setSettings({
          header_style: 'style2',
          button_text: 'Get in Touch',
          button_link: '#',
          text_color: '#ffffff',
          button_color: '#ff6b35',
          button_hover_color: '#ff8a5c',
          is_active: true,
        });
        setMenuItems(fallbackMenuItems);
      }
    };
    fetchData();

    const handleUpdate = () => fetchData();
    window.addEventListener('header-settings-updated', handleUpdate);
    window.addEventListener('custom-links-updated', handleUpdate);

    return () => {
      window.removeEventListener('header-settings-updated', handleUpdate);
      window.removeEventListener('custom-links-updated', handleUpdate);
    };
  }, []);

  useEffect(() => {
    if (location.pathname === '/' && location.state && (location.state as any).scrollTo) {
      const scrollTo = (location.state as any).scrollTo;
      setTimeout(() => {
        const element = document.getElementById(scrollTo);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
          window.history.replaceState({}, document.title);
        }
      }, 100);
    }
  }, [location]);

  const formatUrl = (url: string | undefined): { url: string; isExternal: boolean } => {
    if (!url) return { url: '/', isExternal: false };
    
    if (/^(https?:\/\/|\/\/)/i.test(url)) {
      return { url, isExternal: true };
    }
    
    if (/^www\./i.test(url)) {
      return { url: `https://${url}`, isExternal: true };
    }
    
    if (url.includes('.') && !url.startsWith('/') && !url.startsWith('#')) {
      return { url: `https://${url}`, isExternal: true };
    }
    
    if (url.startsWith('#')) {
      return { url, isExternal: false };
    }
    
    const cleanSlug = url.replace(/^\//, '').toLowerCase();
    if (cleanSlug === 'home' || cleanSlug === '') return { url: '/', isExternal: false };
    
    const internalRoutes = ['/services', '/projects', '/portfolio', '/about', '/contact'];
    if (internalRoutes.includes(url) || internalRoutes.includes('/' + cleanSlug)) {
      return { url: url.startsWith('/') ? url : '/' + cleanSlug, isExternal: false };
    }

    if (url.startsWith('/')) return { url, isExternal: false };
    
    return { url: `/page/${cleanSlug}`, isExternal: false };
  };

  const handleNavigation = (e: React.MouseEvent, item: HeaderMenuItem) => {
    const { url, isExternal } = formatUrl(item.custom_url || item.page_slug);

    if (isExternal) {
      return; 
    }

    e.preventDefault();
    
    if (url.startsWith('#')) {
      const id = url.substring(1);
      if (location.pathname === '/') {
        const element = document.getElementById(id);
        if (element) {
          const y = element.getBoundingClientRect().top + window.scrollY - 80;
          window.scrollTo({ top: y, behavior: 'smooth' });
        }
      } else {
        navigate('/', { state: { scrollTo: id } });
      }
      setMobileMenuOpen(false);
      return;
    }

    const slug = (item.page_slug || '').replace(/^\//, '').toLowerCase();
    const text = (item.menu_text || '').toLowerCase();
    const customUrl = (item.custom_url || '').replace(/^\//, '').toLowerCase();

    if (url === '/' || slug === 'home' || text === 'home' || customUrl === 'home') {
      navigate('/');
      setMobileMenuOpen(false);
      return;
    }
    
    if (slug === 'services' || text === 'services' || customUrl === 'services') {
      navigate('/services');
      setMobileMenuOpen(false);
      return;
    }

    if (slug === 'about' || text === 'about' || customUrl === 'about') {
      navigate('/about');
      setMobileMenuOpen(false);
      return;
    }

    if (slug === 'portfolio' || text === 'portfolio' || customUrl === 'portfolio') {
      navigate('/portfolio');
      setMobileMenuOpen(false);
      return;
    }

    if (slug === 'projects' || text === 'projects' || customUrl === 'projects' ||
        slug === 'project' || text === 'project' || customUrl === 'project') {
      navigate('/projects');
      setMobileMenuOpen(false);
      return;
    }

    navigate(url);
    setMobileMenuOpen(false);
  };

  if (settings && settings.is_active === false) return null;

  const textColor = settings?.text_color || '#ffffff';
  const buttonColor = '#FFBF00';
  const buttonHoverColor = '#e6ac00';

  const getHeaderClasses = () => {
    const base = "fixed z-50 transition-all duration-500";
    
    if (settings?.header_style === 'style2') {
      return `${base} top-0 left-0 right-0 py-4 bg-transparent border-b border-transparent`;
    } else if (settings?.header_style === 'style3') {
      return `${base} top-4 left-4 right-4 md:left-10 md:right-10 rounded-2xl border border-white/10 backdrop-blur-xl bg-[#000000]/80 shadow-2xl py-3`;
    }
    
    return `${base} top-0 left-0 right-0 border-b ${
      isScrolled 
        ? "py-4 backdrop-blur-xl border-white/10 shadow-[0_4px_30px_rgba(0,0,0,0.1)] bg-[#000000]/80" 
        : "py-6 bg-transparent border-transparent"
    }`;
  };

  const handleCtaClick = () => {
    const link = settings?.button_link || '';
    if (!link || link === '#') {
      onContactClick();
    } else if (link.startsWith('#')) {
      const id = link.replace(/^#/, '');
      if (location.pathname === '/') {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      } else {
        navigate('/', { state: { scrollTo: id } });
      }
    } else {
      navigate(link);
    }
  };

  const renderDrawerLinks = (mobile: boolean) => (
    <div className={mobile ? 'flex flex-col gap-6' : 'flex flex-col gap-2'}>
      {menuItems.map((item, idx) => {
        const { url, isExternal } = formatUrl(item.custom_url || item.page_slug);

        if (isExternal) {
          return (
            <motion.div key={item.id} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: idx * 0.06 }}>
              <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className={mobile ? 'text-4xl font-display font-bold transition-all duration-300' : 'block rounded-xl px-4 py-3 text-lg font-semibold transition-all duration-300 hover:bg-white/10'}
                style={{ color: textColor }}
              >
                {item.menu_text}
              </a>
            </motion.div>
          );
        }

        return (
          <motion.div key={item.id} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: idx * 0.06 }}>
            <Link
              to={url}
              onClick={(e) => {
                handleNavigation(e, item);
                setMobileMenuOpen(false);
              }}
              className={mobile ? 'text-4xl font-display font-bold transition-all duration-300' : 'block rounded-xl px-4 py-3 text-lg font-semibold transition-all duration-300 hover:bg-white/10'}
              style={{ color: textColor }}
            >
              {item.menu_text}
            </Link>
          </motion.div>
        );
      })}
    </div>
  );

  const renderStyle1 = () => (
    <div className="container mx-auto px-6 flex justify-between items-center relative">
      <Link 
        to="/" 
        className="flex items-center gap-3 group relative z-10"
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
      >
        {settings?.logo_url ? (
          <img src={assetUrl(settings.logo_url)} alt="Soltechy" className="h-12 md:h-16 w-auto" />
        ) : (
          <>
            <div className="relative w-10 h-10 flex items-center justify-center">
              <div 
                className="absolute inset-0 blur-lg opacity-50 group-hover:opacity-100 transition-opacity duration-500 rounded-full"
                style={{ backgroundColor: buttonColor }}
              ></div>
              <div className="relative z-10 bg-black/50 p-2 rounded-lg border border-white/10 group-hover:border-white/30 transition-colors">
                  <Zap className="w-5 h-5" style={{ color: textColor }} fill="currentColor" />
              </div>
            </div>
            <span className="text-3xl font-display font-bold tracking-tight" style={{ color: textColor }}>
              SOLTECHY
            </span>
          </>
        )}
      </Link>

      <div className="hidden md:flex items-center gap-1">
        {menuItems.map((item) => {
          const { url, isExternal } = formatUrl(item.custom_url || item.page_slug);
          
          if (isExternal) {
            return (
              <a
                key={item.id}
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                onMouseEnter={() => setHoveredLink(item.menu_text)}
                onMouseLeave={() => setHoveredLink(null)}
                className="relative px-6 py-2 text-sm font-medium transition-colors"
                style={{ color: hoveredLink === item.menu_text ? buttonHoverColor : textColor }}
              >
                {item.menu_text}
                {hoveredLink === item.menu_text && (
                  <motion.div
                    layoutId="navbar-hover-1"
                    className="absolute inset-0 bg-white/5 rounded-full -z-10"
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    transition={{ duration: 0.2 }}
                  />
                )}
              </a>
            );
          }

          return (
            <Link
              key={item.id}
              to={url}
              onClick={(e) => handleNavigation(e, item)}
              onMouseEnter={() => setHoveredLink(item.menu_text)}
              onMouseLeave={() => setHoveredLink(null)}
              className="relative px-6 py-2 text-sm font-medium transition-colors"
              style={{ color: hoveredLink === item.menu_text ? buttonHoverColor : textColor }}
            >
              {item.menu_text}
              {hoveredLink === item.menu_text && (
                <motion.div
                  layoutId="navbar-hover-1"
                  className="absolute inset-0 bg-white/5 rounded-full -z-10"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  transition={{ duration: 0.2 }}
                />
              )}
            </Link>
          );
        })}
      </div>

      {settings?.button_text && (
        <div className="hidden md:flex items-center gap-4">
          <button
            onClick={handleCtaClick}
            className="group relative px-6 py-2.5 rounded-full font-bold text-sm overflow-hidden transition-transform hover:scale-105 text-white"
            style={{ backgroundColor: buttonColor }}
          >
            <span className="relative z-10 flex items-center gap-2">
              {settings.button_text}
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </span>
          </button>
        </div>
      )}

      <button 
        className="md:hidden relative z-50 p-2"
        style={{ color: textColor }}
        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
      >
        {mobileMenuOpen ? <X /> : <Menu />}
      </button>
    </div>
  );

  const renderStyle2 = () => (
    <div className="container mx-auto px-4 md:px-6 grid grid-cols-[auto_1fr_auto] items-center relative min-h-[72px] md:min-h-[88px] gap-3">
      <div className="flex justify-start">
         <button 
            className="relative z-50 p-2 group flex items-center gap-2"
            style={{ color: textColor }}
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            onMouseEnter={() => {
              if (window.innerWidth >= 768) setMobileMenuOpen(true);
            }}
          >
            <div className="space-y-1.5 group-hover:space-y-2 transition-all">
              <div className="w-6 h-0.5 bg-current"></div>
              <div className="w-4 h-0.5 bg-current group-hover:w-6 transition-all"></div>
              <div className="w-6 h-0.5 bg-current"></div>
            </div>
            <span className="hidden md:inline font-medium text-sm">MENU</span>
          </button>
      </div>

      {/* Logo section - AUR BADA KIYA GAYA */}
      <div className="flex justify-center min-w-0">
        <Link 
          to="/" 
          className="flex items-center justify-center gap-3 group relative z-10 min-w-0"
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        >
          {settings?.logo_url ? (
            <img 
              src={assetUrl(settings.logo_url)} 
              alt="Soltechy" 
              // 🔥 Logo image ka size aur bada: h-20 md:h-28, max-width bhi increase
              className="h-20 md:h-28 w-auto max-w-[240px] md:max-w-[340px] object-contain" 
            />
          ) : (
            <>
              {/* 🔥 Zap icon ka wrapper aur icon size bada */}
              <div className="relative w-14 h-14 flex items-center justify-center">
                 <Zap className="w-10 h-10" style={{ color: textColor }} fill="currentColor" />
              </div>
              {/* 🔥 Text logo ka font size aur bada */}
              <span className="text-4xl md:text-5xl font-display font-bold tracking-tight" style={{ color: textColor }}>
                SOLTECHY
              </span>
            </>
          )}
        </Link>
      </div>

      <div className="hidden md:flex justify-end">
        <a
          href={whatsappNumber ? `https://wa.me/${whatsappNumber}` : '#'}
          target="_blank"
          rel="noopener noreferrer"
          className="group relative p-3 rounded-full border border-amber-500/30 font-bold overflow-hidden transition-all hover:scale-110 text-white shadow-[0_10px_30px_rgba(255,191,0,0.25)]"
          style={{ backgroundColor: buttonColor }}
        >
          <svg className="w-7 h-7" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
          </svg>
        </a>
      </div>
    </div>
  );

  const renderStyle3 = () => (
    <div className="container mx-auto px-2 flex justify-between items-center relative">
      <Link 
        to="/" 
        className="flex items-center gap-2 group relative z-10 pl-2"
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
      >
        {settings?.logo_url ? (
          <img src={assetUrl(settings.logo_url)} alt="Soltechy" className="h-10 md:h-12 w-auto" />
        ) : (
          <>
            <div className="w-10 h-10 rounded bg-gradient-to-br from-white/20 to-white/5 border border-white/10 flex items-center justify-center">
                 <Zap className="w-5 h-5" style={{ color: textColor }} fill="currentColor" />
            </div>
            <span className="text-xl font-bold tracking-tight" style={{ color: textColor }}>
              SOLTECHY
            </span>
          </>
        )}
      </Link>

      <div className="hidden md:flex items-center gap-1 bg-white/5 rounded-full px-2 py-1 border border-white/5">
        {menuItems.map((item) => {
          const { url, isExternal } = formatUrl(item.custom_url || item.page_slug);
          
          if (isExternal) {
            return (
              <a
                key={item.id}
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                onMouseEnter={() => setHoveredLink(item.menu_text)}
                onMouseLeave={() => setHoveredLink(null)}
                className="relative px-5 py-1.5 text-xs font-bold uppercase tracking-wider transition-colors rounded-full"
                style={{ color: hoveredLink === item.menu_text ? textColor : textColor, opacity: hoveredLink === item.menu_text ? 1 : 0.7 }}
              >
                {item.menu_text}
                {hoveredLink === item.menu_text && (
                  <motion.div
                    layoutId="navbar-hover-3"
                    className="absolute inset-0 bg-white/10 rounded-full -z-10"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                  />
                )}
              </a>
            );
          }
          
          return (
            <Link
              key={item.id}
              to={url}
              onClick={(e) => handleNavigation(e, item)}
              onMouseEnter={() => setHoveredLink(item.menu_text)}
              onMouseLeave={() => setHoveredLink(null)}
              className="relative px-5 py-1.5 text-xs font-bold uppercase tracking-wider transition-colors rounded-full"
              style={{ color: hoveredLink === item.menu_text ? textColor : textColor, opacity: hoveredLink === item.menu_text ? 1 : 0.7 }}
            >
              {item.menu_text}
              {hoveredLink === item.menu_text && (
                <motion.div
                  layoutId="navbar-hover-3"
                  className="absolute inset-0 bg-white/10 rounded-full -z-10"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                />
              )}
            </Link>
          );
        })}
      </div>

      <div className="flex items-center gap-3 pr-2">
        {settings?.button_text && (
          <button
            onClick={handleCtaClick}
            className="hidden md:flex group relative px-5 py-2 rounded-lg font-bold text-xs uppercase tracking-wider overflow-hidden text-white bg-white/10 hover:bg-white/20 transition-colors"
            style={{ backgroundColor: buttonColor }}
          >
            <span>{settings.button_text}</span>
          </button>
        )}
        
        <div className="flex items-center gap-3 md:hidden">
          <a
            href={whatsappNumber ? `https://wa.me/${whatsappNumber}` : '#'}
            target="_blank"
            rel="noopener noreferrer"
            className="relative p-3 rounded-full border-2 border-amber-500 font-bold overflow-hidden transition-all hover:scale-110 text-white shadow-lg"
            style={{ backgroundColor: '#FFBF00' }}
          >
            <svg className="w-7 h-7" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
            </svg>
          </a>
          <button
            className="relative z-50 p-2"
            style={{ color: textColor }}
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>
    </div>
  );

  const getAnimationProps = () => {
    const type = settings?.animation_type || 'slide';
    if (type === 'fade') return { initial: { opacity: 0 }, animate: { opacity: 1 } };
    if (type === 'none') return { initial: {}, animate: {} };
    return { initial: { y: -100 }, animate: { y: 0 } };
  };

  return (
    <>
      <style>{settings?.custom_css}</style>
      <motion.nav
        {...getAnimationProps()}
        className={getHeaderClasses()}
      >
        {renderStyle2()}
      </motion.nav>

      <AnimatePresence>
        {mobileMenuOpen && settings?.header_style === 'style2' && (
          <motion.div
            initial={{ opacity: 0, x: -24 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -24 }}
            onMouseEnter={() => {
              if (window.innerWidth >= 768) setMobileMenuOpen(true);
            }}
            onMouseLeave={() => {
              if (window.innerWidth >= 768) setMobileMenuOpen(false);
            }}
            className="hidden md:block fixed left-6 top-24 z-40 w-[320px] rounded-[2rem] border border-white/10 bg-[#000000]/95 backdrop-blur-2xl shadow-[0_24px_80px_rgba(0,0,0,0.45)] p-5"
          >
            {renderDrawerLinks(false)}
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="fixed inset-0 z-40 backdrop-blur-xl md:hidden flex flex-col pt-24 px-6"
            style={{ backgroundColor: '#000000' }}
          >
            <div className="flex flex-col gap-6">
              {renderDrawerLinks(true)}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};